// BuildingStabilityInfoComponent.js
import { useEffect, useState } from "react";
import {
  SectionHeader,
  EditableSelectComponent,
  EditableTextareaComponent,
} from "components";
import { Document, Eye, Plus } from "assets/icons"; // Replace with appropriate icons
import { useSession, useUser } from "@clerk/clerk-react";

// Assuming BuildingStabilityInfoDTO is defined elsewhere in your application
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  BuildingOwnerShipInfo,
  EasementCheckStatus,
  PreEmptionRightPresent,
} from "shared/types/ownership-info";
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@heroui/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { DominantOrPassiveEstate } from "shared/types/ownership-info";
import FileLabelSearchComponent from "shared/components/searchBar/components/fileLabelSearchComponent";
import { FileLogStatus, SignStatus } from "shared/types/file";

interface BuildingEasementsInfoComponentProps {
  ownershipInfo: BuildingOwnerShipInfo;
  handleInspectionInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  building: BuildingDTO;
  unit: UnitDTO;
  parentEditMode?: boolean;
}

const BuildingEasementsInfoComponent = ({
  ownershipInfo,
  handleCloseParent,
  handleInspectionInfoChangeInParent,
  parentEditMode = true,
}: BuildingEasementsInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedOwnershipInfo, setEditedOwnershipInfo] = useState(ownershipInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAddEasementModalOpen, setIsAddEasementModalOpen] = useState(false);
  const [newEasement, setNewEasement] = useState<any>({
    dominantOrPassiveEstate: DominantOrPassiveEstate.UNKNOWN,
    buildingEasementDescription: "",
    easementExcerptDescription: "",
    excerptFileLogId: null,
    excerptFileLog: null,
  });
  const { session } = useSession();
  const { user } = useUser();
  const [selectedLabel, setSelectedLabel] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [pastedImage, setPastedImage] = useState<{ url: string; file: File } | null>(null);
  const [searchLabel, setSearchLabel] = useState<string>("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isEditingEasement, setIsEditingEasement] = useState<boolean>(false);
  const [editingEasementIndex, setEditingEasementIndex] = useState<number | null>(null);
  // Derived UI states
  const needsLabelForUpload = !selectedLabel;
  const hasPendingUnuploadedPastedImage = !!pastedImage && !newEasement.excerptFileLogId;

  useEffect(() => {
    // Keep local state in sync with parent-provided ownershipInfo
    setEditedOwnershipInfo(prev => {
      // If we are not editing locally and parent has fresher data, replace
      if (!hasUnsavedChanges) return ownershipInfo;
      // If we have unsaved local changes, merge server updates conservatively
      return {
        ...ownershipInfo,
        easements: Array.isArray(prev?.easements) ? prev.easements : ownershipInfo?.easements
      } as any;
    });
  }, []);

  const handleOpenAddEasementModal = () => {
    setIsAddEasementModalOpen(true);
    console.log(isAddEasementModalOpen);
    // ensure a fresh start every time
    setSelectedLabel("");
    setSearchLabel("");
    setSearchResults([]);
  };

  const startEditEasement = (easement: any, index: number) => {
    setNewEasement({
      dominantOrPassiveEstate: easement.dominantOrPassiveEstate || DominantOrPassiveEstate.UNKNOWN,
      buildingEasementDescription: easement.buildingEasementDescription || "",
      easementExcerptDescription: easement.easementExcerptDescription || "",
      excerptFileLogId: easement.excerptFileLogId || (easement.excerptFileLog && easement.excerptFileLog.id) || null,
      excerptFileLog: easement.excerptFileLog || null,
    });
    setIsEditingEasement(true);
    setEditingEasementIndex(index);
    setIsAddEasementModalOpen(true);
  };

  const handleCloseAddEasementModal = () => {
    setIsAddEasementModalOpen(false);
    setNewEasement({
      dominantOrPassiveEstate: DominantOrPassiveEstate.UNKNOWN,
      buildingEasementDescription: "",
      easementExcerptDescription: "",
      excerptFileLogId: null,
      excerptFileLog: null,
    });
    setSelectedLabel("");
    setSearchLabel("");
    setPastedImage(null);
    setSearchResults([]);
    setIsEditingEasement(false);
    setEditingEasementIndex(null);
  };

  const handleSaveNewEasementLocally = () => {
    console.log("💾 [handleSaveNewEasementLocally] Saving easement:", newEasement);
    const easements = Array.isArray(editedOwnershipInfo.easements)
      ? editedOwnershipInfo.easements
      : [];
    let updatedEasements: any[];
    if (isEditingEasement && editingEasementIndex !== null && editingEasementIndex >= 0) {
      updatedEasements = easements.map((e, idx) => idx === editingEasementIndex ? { ...newEasement } : e);
      console.log("💾 [handleSaveNewEasementLocally] Updating easement at index:", editingEasementIndex, newEasement);
    } else {
      const easementToAdd = { ...newEasement } as any;
      console.log("💾 [handleSaveNewEasementLocally] Easement to add:", easementToAdd);
      updatedEasements = [...easements, easementToAdd];
    }
    const updated = {
      ...editedOwnershipInfo,
      easements: updatedEasements,
    } as any;
    setEditedOwnershipInfo((prev: any) => ({
      ...prev,
      easements: updatedEasements,
    }));
    setHasUnsavedChanges(true);
    const persist = async () => {
      try {
        console.log("💾 [handleSaveNewEasementLocally] Persisting updated ownership info:", updated);
        await fetchWithAuth(
          `${API_URL}/building/building-ownership-info`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...updated, buildingId: (ownershipInfo as any)?.buildingId || (ownershipInfo as any)?.building?.id }),
          },
          session,
        );
        // Refresh ownership info from building to get IDs and populated file logs
        const buildingId = (ownershipInfo as any)?.buildingId || (ownershipInfo as any)?.building?.id;
        if (buildingId) {
          console.log("💾 [handleSaveNewEasementLocally] Refreshing building data for ID:", buildingId);
          const bResp = await fetchWithAuth(`${API_URL}/building/${buildingId}`, { method: "GET" }, session);
          const freshBuilding = await bResp.json();
          console.log("💾 [handleSaveNewEasementLocally] Fresh building data:", freshBuilding?.buildingOwnershipInfo);
          if (freshBuilding?.buildingOwnershipInfo) {
            setEditedOwnershipInfo(freshBuilding.buildingOwnershipInfo);
            setHasUnsavedChanges(false);
          }
        }
        toast.success("Erfdienstbaarheid opgeslagen.");
      } catch (e) {
        console.error("💾 [handleSaveNewEasementLocally] Error:", e);
        toast.error("Opslaan erfdienstbaarheid mislukt.");
      } finally {
        handleCloseAddEasementModal();
      }
    };
    persist();
  };

  const openExcerptById = async (fileLogId: number) => {
    try {
      const resp = await fetchWithAuth(`${API_URL}/file/file-log-with-epc/${fileLogId}`, { method: "GET" }, session);
      const data = await resp.json();
      const url = data?.sharePointFileUrl;
      if (url) {
        window.open(url, '_blank', 'noopener');
      } else {
        toast.error('Geen URL gevonden voor dit document.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Kon het document niet openen.');
    }
  };

  const handlePaste = async (event: React.ClipboardEvent<HTMLDivElement>) => {
    console.log("📋 [handlePaste] Paste event triggered");
    const items = event.clipboardData?.items;
    console.log("📋 [handlePaste] Clipboard items:", items?.length);
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      console.log("📋 [handlePaste] Item type:", item.type);
      if (item.type.indexOf("image") !== -1) {
        const blob = item.getAsFile();
        console.log("📋 [handlePaste] Got blob:", !!blob);
        if (blob) {
          const file = new File([blob], `easement-excerpt-${Date.now()}.jpg`, { type: blob.type || "image/jpeg" });
          const url = URL.createObjectURL(file);
          console.log("📋 [handlePaste] Created file and URL:", file.name, url);
          setPastedImage({ url, file });
        }
      }
    }
  };

  const uploadFileLog = async (file: File, label: string) => {
    console.log("📤 [uploadFileLog] Starting upload - file:", file.name, "label:", label);
    if (!ownershipInfo?.buildingId && !(ownershipInfo as any)?.building?.id) {
      toast.error("Geen gebouw gekoppeld.");
      return null;
    }
    if (!label) {
      toast.error("Kies eerst een bestandslabel.");
      return null;
    }
    setIsUploading(true);
    try {
      const form = new FormData();
      const buildingId = ownershipInfo.buildingId || (ownershipInfo as any).building?.id;
      console.log("📤 [uploadFileLog] Building ID:", buildingId);
      const fileLog = {
        fileLabel: label,
        buildingId,
        status: FileLogStatus.UNKNOWN,
        signStatus: SignStatus.UNKNOWN,
        createdByUserId: user?.id,
        createdByUserFullName: user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : undefined,
        secondaryLabels: [],
      } as any;
      console.log("📤 [uploadFileLog] FileLog payload:", fileLog);
      form.append("file", file);
      form.append("fileLog", JSON.stringify(fileLog));
      const res = await fetchWithAuth(
        `${API_URL}/file/log`,
        { method: "POST", body: form },
        session
      );
      console.log("📤 [uploadFileLog] Response status:", res.status);
      const data = await res.json();
      console.log("📤 [uploadFileLog] Response data:", data);
      const savedFileLog = data?.fileLog || data;
      console.log("📤 [uploadFileLog] Saved FileLog:", savedFileLog);
      if (!savedFileLog?.id) throw new Error("Upload mislukt");
      return savedFileLog;
    } catch (e) {
      console.error("📤 [uploadFileLog] Error:", e);
      toast.error("Upload mislukt.");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadPasted = async () => {
    if (!pastedImage) return;
    console.log("🔄 [handleUploadPasted] Starting upload with label:", selectedLabel);
    setIsUploading(true);
    const fileLog = await uploadFileLog(pastedImage.file, selectedLabel);
    console.log("🔄 [handleUploadPasted] Upload result:", fileLog);
    if (fileLog) {
      console.log("🔄 [handleUploadPasted] Setting easement with fileLogId:", fileLog.id);
      setNewEasement((prev: any) => {
        const updated = {
          ...prev,
          excerptFileLogId: fileLog.id,
          excerptFileLog: fileLog,
        };
        console.log("🔄 [handleUploadPasted] Updated easement:", updated);
        return updated;
      });
      toast.success("Document gekoppeld aan erfdienstbaarheid.");
    }
    setIsUploading(false);
  };

  const handleUploadInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fileLog = await uploadFileLog(file, selectedLabel);
    if (fileLog) {
      setNewEasement((prev: any) => ({
        ...prev,
        excerptFileLogId: fileLog.id,
        excerptFileLog: fileLog,
      }));
      toast.success("Document gekoppeld aan erfdienstbaarheid.");
    }
    e.target.value = "";
  };

  const handleSearchExisting = async (label: string) => {
    if (!ownershipInfo?.buildingId && !(ownershipInfo as any)?.building?.id) return;
    const buildingId = ownershipInfo.buildingId || (ownershipInfo as any).building?.id;
    setIsSearching(true);
    try {
      const resp = await fetchWithAuth(
        `${API_URL}/file/get-files?buildingId=${buildingId}&fileLabel=${encodeURIComponent(label)}&page=1&pageSize=10`,
        { method: "GET" },
        session,
      );
      const data = await resp.json();
      setSearchResults(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) {
      console.error(e);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const linkExistingFileLog = (fileLog: any) => {
    setNewEasement((prev: any) => ({
      ...prev,
      excerptFileLogId: fileLog.id,
      excerptFileLog: fileLog,
    }));
    toast.success("Bestaand document gekoppeld.");
  };

  const handleSave = async () => {
    console.log("💾 [handleSave] Starting save process");
    console.log("💾 [handleSave] Current editedOwnershipInfo:", editedOwnershipInfo);
    setHasUnsavedChanges(false);
    try {
      const sanitize = (info: any) => ({
        id: info?.id,
        buildingId: info?.buildingId || info?.building?.id || (ownershipInfo as any)?.buildingId || (ownershipInfo as any)?.building?.id,
        preEmptionRightPresent: info?.preEmptionRightPresent,
        preEmptionRightPresentDescription: info?.preEmptionRightPresentDescription,
        easementCheckStatus: info?.easementCheckStatus,
        appraisalFileLogIds: info?.appraisalFileLogIds,
        buildingDeedsFileLogIds: info?.buildingDeedsFileLogIds,
        appraisalValue: info?.appraisalValue,
        buildingInsuredValue: info?.buildingInsuredValue,
        easements: (info?.easements || []).map((e: any) => ({
          id: e?.id,
          dominantOrPassiveEstate: e?.dominantOrPassiveEstate,
          buildingEasementDescription: e?.buildingEasementDescription,
          easementExcerptDescription: e?.easementExcerptDescription,
          excerptFileLogId: e?.excerptFileLogId || (e?.excerptFileLog && e?.excerptFileLog.id) || null,
        })),
      });
      const payload = sanitize(editedOwnershipInfo);
      console.log("💾 [handleSave] Sanitized payload:", payload);
      console.log("💾 [handleSave] easementCheckStatus in payload:", payload.easementCheckStatus);
      
      const resp = await fetchWithAuth(
        `${API_URL}/building/building-ownership-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        session,
      );
      console.log("💾 [handleSave] Response status:", resp.status);
      if (!resp.ok) {
        const t = await resp.text();
        console.error("💾 [handleSave] Error response:", t);
        throw new Error(`Opslaan mislukt (${resp.status}): ${t}`);
      }
      const responseData = await resp.json();
      console.log("💾 [handleSave] Success response:", responseData);
      
      // Update the state with the saved data to reflect database changes
      if (responseData) {
        console.log("💾 [handleSave] Updating local state with saved data:", responseData);
        setEditedOwnershipInfo(responseData);
      }
      
      toast.success("Eigenaars informatie succesvol bijgewerkt!");
    } catch (e) {
      console.error("💾 [handleSave] Error:", e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de eigendoms informatie.",
      );
    }
  };

  const updateOwnershipInfo = (updatedFields: any) => {
    console.log("🔧 [updateOwnershipInfo] Updating ownership info with:", updatedFields);
    console.log("🔧 [updateOwnershipInfo] Previous state:", editedOwnershipInfo);
    setHasUnsavedChanges(true);
    setEditedOwnershipInfo((prev: any) => {
      const newState = { ...prev, ...updatedFields };
      console.log("🔧 [updateOwnershipInfo] New state:", newState);
      return newState;
    });

    if (handleInspectionInfoChangeInParent) {
      handleInspectionInfoChangeInParent({
        ...ownershipInfo,
        ...updatedFields,
      });
    }
  };

  // const handleFileModalClose = () => {
  //     setIsAddEasementModalOpen(false);
  // };

  const getStatusColor = (status: any) => {
    switch (status) {
      case EasementCheckStatus.OK:
        return "bg-green-500 shadow-sm";
      case EasementCheckStatus.UNKNOWN:
        return "bg-yellow-500 shadow-sm";
      default:
        return "bg-gray-300";
    }
  };

  const updatePreEmptionRightPresentDescription = (value: string) => {
    updateOwnershipInfo({ preEmptionRightPresentDescription: value });
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Eigendomsinformatie"
        icon={<Document />}
        editMode={editMode}
        sticky={false}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={() => handleSave()}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={handleCloseParent}
        hideCloseAction={true}
      />
      <div className="p-6">
        <div className="space-y-6">
          {/* Easement Check Status */}
          <div className="rounded-lg border border-gray-200 bg-linear-to-br from-green-50/50 to-emerald-50/30 p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block h-3 w-3 rounded-full ${getStatusColor(
                    editedOwnershipInfo.easementCheckStatus,
                  )}`}
                />
                <h3 className="text-lg font-semibold text-neutral-primary">Controle Status</h3>
              </div>
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-neutral-primary">
                Erfdienstbaarheden nagekeken in laatste notariële akte?
              </label>
              <EditableSelectComponent
                label="Selecteer status..."
                options={Object.values(EasementCheckStatus).map((value) => ({
                  label: value,
                  value,
                }))}
                value={editedOwnershipInfo.easementCheckStatus}
                editMode={editMode}
                onChange={(value) =>
                  updateOwnershipInfo({ easementCheckStatus: value })
                }
              />
              {editedOwnershipInfo.easementCheckStatus === EasementCheckStatus.OK && (
                <div className="mt-3 rounded-md bg-green-100 border border-green-200 p-3">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium text-green-800">
                      Erfdienstbaarheden zijn gecontroleerd en in orde
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Description for PreEmption Right (if needed) */}
          {editedOwnershipInfo.preEmptionRightPresent ===
            PreEmptionRightPresent.NOT_OK && (
            <div className="rounded-lg border border-orange-200 bg-orange-50/30 p-6">
              <h3 className="mb-4 text-lg font-semibold text-neutral-primary">Voorkooprecht Details</h3>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-neutral-primary">
                  Beschrijving van het voorkooprecht
                </label>
                <EditableTextareaComponent
                  label="Beschrijf de situatie omtrent het voorkooprecht..."
                  value={
                    editedOwnershipInfo.preEmptionRightPresentDescription || ""
                  }
                  editMode={editMode}
                  onChange={(value) =>
                    updatePreEmptionRightPresentDescription(value)
                  }
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 p-2">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-primary">Erfdienstbaarheden Overzicht</h3>
                <p className="text-sm text-neutral-secondary-mid">
                  {editedOwnershipInfo.easements?.length || 0} erfdienstbaarhe{(editedOwnershipInfo.easements?.length || 0) === 1 ? 'd' : 'den'} geregistreerd
                </p>
              </div>
            </div>
            <ButtonNew
              onClick={handleOpenAddEasementModal}
              icon={<Plus />}
              label="Nieuwe erfdienstbaarheid"
              variant="primaryMoss"
            />
          </div>
          
          {editedOwnershipInfo.easements && editedOwnershipInfo.easements.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <Table 
                aria-label="Erfdienstbaarheden overzicht"
                classNames={{
                  wrapper: "shadow-none rounded-none",
                  th: "bg-gray-50 text-neutral-primary font-medium text-sm",
                  td: "py-4"
                }}
              >
                <TableHeader>
                  <TableColumn>TYPE</TableColumn>
                  <TableColumn>BESCHRIJVING</TableColumn>
                  <TableColumn>DOCUMENT</TableColumn>
                  <TableColumn>ACTIES</TableColumn>
                </TableHeader>
                <TableBody>
                  {editedOwnershipInfo.easements.map((easement, index) => (
                    <TableRow key={easement.id || index} className="hover:bg-gray-50/50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${
                            easement.dominantOrPassiveEstate === 'Heersend erf' 
                              ? 'bg-green-500' 
                              : easement.dominantOrPassiveEstate === 'Lijdend erf' 
                                ? 'bg-orange-500' 
                                : 'bg-gray-400'
                          }`}></div>
                          <span className="text-sm font-medium text-neutral-primary">
                            {easement.dominantOrPassiveEstate}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="text-sm text-neutral-primary line-clamp-2">
                            {easement.buildingEasementDescription || (
                              <span className="text-neutral-secondary-mid italic">Geen beschrijving</span>
                            )}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {easement.excerptFileLog ? (
                          <div className="flex items-center gap-2">
                            <div className="rounded-sm bg-green-100 p-1">
                              <Document className="h-4 w-4 text-green-600" />
                            </div>
                            <a
                              href={easement.excerptFileLog.sharePointFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-green-600 hover:text-green-700 hover:underline"
                            >
                              Document bekijken
                            </a>
                          </div>
                        ) : easement.excerptFileLogId ? (
                          <div className="flex items-center gap-2">
                            <div className="rounded-sm bg-blue-100 p-1">
                              <Document className="h-4 w-4 text-blue-600" />
                            </div>
                            <button
                              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                              onClick={() => openExcerptById(easement.excerptFileLogId)}
                            >
                              Open gekoppeld document
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="rounded-sm bg-gray-100 p-1">
                              <Document className="h-4 w-4 text-gray-400" />
                            </div>
                            <span className="text-sm text-neutral-secondary-mid">Geen document</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {easement.excerptFileLog && (
                            <Button
                              size="sm"
                              variant="flat"
                              className="border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
                              startContent={<Eye className="h-3 w-3" />}
                              as="a"
                              href={easement.excerptFileLog.sharePointFileUrl}
                              target="_blank"
                            >
                              Bekijk
                            </Button>
                          )}
                          {easement.excerptFileLogId && !easement.excerptFileLog && (
                            <Button
                              size="sm"
                              variant="flat"
                              className="border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
                              startContent={<Eye className="h-3 w-3" />}
                              onClick={() => openExcerptById(easement.excerptFileLogId)}
                            >
                              Bekijk
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="flat"
                            className="border border-gray-200 bg-gray-50 text-neutral-primary hover:bg-gray-100"
                            onClick={() => startEditEasement(easement, index)}
                          >
                            Bewerk
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50/30 p-8 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <Eye className="h-6 w-6 text-gray-400" />
              </div>
              <h4 className="mb-2 text-sm font-medium text-neutral-primary">Geen erfdienstbaarheden</h4>
              <p className="mb-4 text-sm text-neutral-secondary-mid">
                Er zijn nog geen erfdienstbaarheden geregistreerd voor dit gebouw.
              </p>
              <ButtonNew
                onClick={handleOpenAddEasementModal}
                icon={<Plus />}
                label="Eerste erfdienstbaarheid toevoegen"
                variant="primaryMoss"
              />
            </div>
          )}
        </div>
        {/* Add Easement Modal */}
        <Modal 
          isOpen={isAddEasementModalOpen} 
          onClose={handleCloseAddEasementModal} 
          backdrop="blur" 
          isDismissable={false} 
          scrollBehavior="inside" 
          size="4xl"
          classNames={{
            base: "max-h-[90vh]",
            body: "p-0",
            header: "border-b border-gray-100 pb-4",
            footer: "border-t border-gray-100 pt-4"
          }}
        >
          <ModalContent className="bg-white">
            <ModalHeader className="flex flex-col gap-1 px-6 pt-6">
              <h2 className="text-xl font-semibold text-neutral-primary">Nieuwe erfdienstbaarheid toevoegen</h2>
              <p className="text-sm text-neutral-secondary-mid">Voeg een nieuwe erfdienstbaarheid toe met bijbehorende documentatie</p>
            </ModalHeader>
            <ModalBody className="px-6 py-6">
              <div className="flex flex-col gap-6">
                {/* Basic Information */}
                <div className="rounded-lg border border-gray-200 bg-gray-50/30 p-4">
                  <h3 className="mb-4 text-sm font-medium text-neutral-primary">Basis informatie</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-neutral-primary">
                        Type erfdienstbaarheid <span className="text-red-500">*</span>
                      </label>
                      <EditableSelectComponent
                        label="Selecteer type..."
                        options={Object.values(DominantOrPassiveEstate).map((value) => ({ label: value, value }))}
                        value={newEasement.dominantOrPassiveEstate}
                        editMode={true}
                        onChange={(value) => setNewEasement((prev: any) => ({ ...prev, dominantOrPassiveEstate: value }))}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-neutral-primary">Status</label>
                      <div className="flex h-10 items-center rounded-md border border-gray-200 bg-white px-3">
                        <span className="text-sm text-neutral-secondary-mid">Nieuw</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description Fields */}
                <div className="rounded-lg border border-gray-200 bg-gray-50/30 p-4">
                  <h3 className="mb-4 text-sm font-medium text-neutral-primary">Beschrijvingen</h3>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-neutral-primary">
                        Beschrijving erfdienstbaarheid
                      </label>
                      <EditableTextareaComponent
                        label="Beschrijf de erfdienstbaarheid in detail..."
                        value={newEasement.buildingEasementDescription}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setNewEasement((prev: any) => ({ ...prev, buildingEasementDescription: value }))}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-neutral-primary">
                        Uittreksel document (beschrijving)
                      </label>
                      <EditableTextareaComponent
                        label="Beschrijf wat er in het uittreksel document staat..."
                        value={newEasement.easementExcerptDescription}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setNewEasement((prev: any) => ({ ...prev, easementExcerptDescription: value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Document Management */}
                <div className="rounded-lg border border-gray-200 bg-linear-to-br from-blue-50/50 to-indigo-50/30 p-4">
                  <h3 className="mb-4 flex items-center gap-2 text-sm font-medium text-neutral-primary">
                    <Document className="h-4 w-4" />
                    Bron-document koppelen
                  </h3>
                  
                  {/* Document Workflow Steps */}
                  <div className="space-y-6">
                    {/* Step 1: Label Selection */}
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-moss-primary text-xs font-medium text-white">1</div>
                        <label className="text-sm font-medium text-neutral-primary">Selecteer bestandslabel</label>
                      </div>
                      <div className="ml-8">
                        <FileLabelSearchComponent
                          incomingSelectedLabel={selectedLabel}
                          onSelect={(lbl) => {
                            const val = typeof lbl === 'string' ? lbl : (lbl as any).label;
                            setSelectedLabel(val);
                            setSearchLabel(val);
                            handleSearchExisting(val);
                          }}
                          incomingResourceTypes={['building']}
                          incomingDocumentTypes={['document']}
                          labelType="full"
                        />
                      </div>
                    </div>

                    {/* Step 2: Upload/Paste */}
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-moss-primary text-xs font-medium text-white">2</div>
                        <label className="text-sm font-medium text-neutral-primary">Upload of plak document</label>
                      </div>
                      <div className="ml-8 space-y-3">
                        {needsLabelForUpload && (
                          <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2">
                            <span className="mt-0.5 inline-block h-2 w-2 rounded-full bg-yellow-500"></span>
                            <p className="text-xs text-yellow-800">Selecteer eerst een bestandslabel om een document te kunnen uploaden.</p>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-neutral-secondary-mid">
                          <span className="rounded-sm bg-neutral-100 px-2 py-0.5">Tip</span>
                          <span>Ondersteunt afbeeldingen en PDF. Je kan ook een screenshot plakken.</span>
                        </div>
                        <div
                          className="group relative flex h-32 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white transition-colors hover:border-moss-primary hover:bg-moss-primary/5"
                          onPaste={handlePaste}
                          tabIndex={0}
                          onClick={() => {
                            console.log("📋 [Paste Area] Clicked - focusing for paste");
                          }}
                          onFocus={() => {
                            console.log("📋 [Paste Area] Focused");
                          }}
                        >
                          {pastedImage ? (
                            <div className="flex flex-col items-center gap-2">
                              <img src={pastedImage.url} alt="Geplakte afbeelding" className="max-h-20 rounded-sm object-contain shadow-sm" />
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-neutral-secondary-mid">Screenshot klaar om te uploaden</span>
                                <button
                                  className="text-xs font-medium text-neutral-primary underline"
                                  onClick={() => setPastedImage(null)}
                                >
                                  Verwijderen
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-2 text-center">
                              <div className="rounded-full bg-gray-100 p-2 group-hover:bg-moss-primary/10">
                                <Document className="h-5 w-5 text-gray-400 group-hover:text-moss-primary" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-neutral-primary">Plak een screenshot (Ctrl/Cmd+V)</p>
                                <p className="text-xs text-neutral-secondary-mid">of sleep een document hierheen</p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex-1">
                            <input 
                              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm file:mr-3 file:rounded-sm file:border-0 file:bg-gray-100 file:px-3 file:py-1 file:text-xs file:font-medium file:text-neutral-primary hover:file:bg-gray-200" 
                              type="file" 
                              accept="image/*,application/pdf" 
                              onChange={handleUploadInputChange} 
                            />
                          </div>
                          <Button 
                            color="primary" 
                            className="bg-moss-primary text-white hover:bg-moss-primary/90" 
                            isDisabled={!pastedImage || needsLabelForUpload || isUploading} 
                            onClick={() => {
                              console.log("🖱️ [Upload Button] Clicked! pastedImage:", !!pastedImage, "selectedLabel:", selectedLabel);
                              handleUploadPasted();
                            }}
                            size="sm"
                          >
                            {isUploading ? 'Bezig met uploaden…' : 'Upload document'}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Step 3: Link Existing */}
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-moss-primary text-xs font-medium text-white">3</div>
                        <label className="text-sm font-medium text-neutral-primary">Of koppel bestaand document</label>
                      </div>
                      <div className="ml-8">
                        {isSearching && (
                          <div className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
                            <span className="text-sm text-blue-700">Documenten zoeken...</span>
                          </div>
                        )}
                        <div className="max-h-48 space-y-2 overflow-auto">
                          {searchResults.map((log: any) => (
                            <div key={log.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md">
                              <div className="flex flex-1 items-center gap-3">
                                <div className="rounded-sm bg-gray-100 p-1">
                                  <Document className="h-4 w-4 text-gray-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <a 
                                    href={log.sharePointFileUrl} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className="block truncate text-sm font-medium text-neutral-primary hover:text-moss-primary hover:underline"
                                  >
                                    {log.summary || (Array.isArray(log.label) ? log.label[0] : log.label) || 'Document'}
                                  </a>
                                  <p className="text-xs text-neutral-secondary-mid">
                                    {Array.isArray(log.label) ? log.label.join(', ') : log.label}
                                  </p>
                                </div>
                              </div>
                              <Button 
                                size="sm" 
                                variant="flat" 
                                className="border border-moss-primary/20 bg-moss-primary/5 text-moss-primary hover:bg-moss-primary hover:text-white" 
                                onClick={() => linkExistingFileLog(log)}
                              >
                                Koppelen
                              </Button>
                            </div>
                          ))}
                          {searchLabel && !isSearching && searchResults.length === 0 && (
                            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3">
                              <p className="text-sm text-yellow-800">
                                Geen documenten gevonden voor label "<span className="font-medium">{searchLabel}</span>".
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Linked Document Display */}
                    {newEasement.excerptFileLog && (
                      <div className="ml-8 rounded-lg border border-green-200 bg-green-50 p-3">
                        <div className="flex items-center gap-2">
                          <div className="rounded-sm bg-green-100 p-1">
                            <Document className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-green-800">Document gekoppeld</p>
                            <p className="text-xs text-green-600">
                              {newEasement.excerptFileLog.summary || 'Document succesvol gekoppeld'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter className="px-6 pb-6">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-neutral-secondary-mid">
                  <div className="rounded-sm bg-blue-100 px-2 py-1">
                    <span className="text-blue-700">💡 Tip</span>
                  </div>
                  <span>Plak snel een screenshot met Ctrl/Cmd+V</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={handleCloseAddEasementModal} 
                    variant="flat" 
                    className="border border-gray-200 bg-white text-neutral-primary hover:bg-gray-50"
                  >
                    Annuleren
                  </Button>
                  <Button 
                    color="primary" 
                    className="bg-moss-primary text-white hover:bg-moss-primary/90 font-medium" 
                    isDisabled={hasPendingUnuploadedPastedImage}
                    onClick={handleSaveNewEasementLocally}
                  >
                    Erfdienstbaarheid toevoegen
                  </Button>
                </div>
              </div>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </section>
  );
};

export default BuildingEasementsInfoComponent;
